import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { Modal, Spinner } from 'react-bootstrap';
import Pagination from 'react-js-pagination';
import { toast } from 'react-toastify';
import { downloadFileByUrls } from '@app/utils/fileDownload';

import {
    projectGetAllPagingAction,
    projectDeleteAction,
    projectUpdateStatusAction
} from '@app/store/reducers/project';
import { URI_INTERNAL } from '@app/utils/pathLocations';
import { messageToast, objectShowNumber } from '@app/utils/define';
import { ConfirmModal } from '../../modules/modal/ConfirmModal';
import {
    TRANG_THAI_DU_AN_OPTIONS,
    mapTrangThaiDuAnLabel
} from '@app/store/constants';
import { teamGetAllAction } from '@app/store/reducers/team';
import { BaseGatewayImage, PROJECT_API } from '@app/utils/apiUrl';
import { Dropdown } from 'react-bootstrap';


function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('vi-VN');
}

const isProjectDone = (status) =>
    status === 1 || status === 'hoanthanh';

const clampPercent = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return 0;
    return Math.max(0, Math.min(100, Math.round(n)));
};

const getProjectActualProgress = (project) =>
    clampPercent(project?.tienDoTongThe);

const getProjectEffectiveProgress = (project) =>
    isProjectDone(project?.trangThai) ? 100 : getProjectActualProgress(project);

const hasMissingExpectedEndDate = (project) => {
    const endDate = project?.endDate;
    return endDate === null || endDate === undefined || String(endDate).trim() === '';
};

const MISSING_END_DATE_COLOR = '#f8d7da';

const MISSING_END_DATE_CELL_STYLE = {
    backgroundColor: MISSING_END_DATE_COLOR,
    '--bs-table-bg': MISSING_END_DATE_COLOR,
    '--bs-table-accent-bg': MISSING_END_DATE_COLOR,
    '--bs-table-striped-bg': MISSING_END_DATE_COLOR,
    '--bs-table-hover-bg': MISSING_END_DATE_COLOR,
    boxShadow: `inset 0 0 0 9999px ${MISSING_END_DATE_COLOR}`
};

const BIG_BADGE_STYLE = {
    fontSize: '0.9rem',
    padding: '0.35rem 0.8rem',
    minWidth: '70px',
    display: 'inline-block',
    fontWeight: '600'
};

const TABLE_HEADER_FOOTER_CELL_STYLE = {
    textAlign: 'center',
    verticalAlign: 'middle'
};

const Project = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    const {
        list,
        loading,
        totalRecords,
        pageIndex: storePageIndex,
        pageSize: storePageSize
    } = useSelector((state) => state.project || {});

    const teamState = useSelector((state) => state.team || {});
    const teams = Array.isArray(teamState.list) ? teamState.list : [];

    const [searchText, setSearchText] = useState('');
    const [startDateFilter, setStartDateFilter] = useState('');
    const [endDateFilter, setEndDateFilter] = useState('');
    const [selectedTeamId, setSelectedTeamId] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');

    // Đọc page/size từ URL ngay khi khởi tạo để giữ trạng thái phân trang khi reload
    const initParams = new URLSearchParams(location.search);
    const initPage = parseInt(initParams.get('page'), 10);
    const initSize = parseInt(initParams.get('size'), 10);

    const [page, setPage] = useState(!isNaN(initPage) && initPage > 0 ? initPage : 1);
    const [pageSize, setPageSize] = useState(!isNaN(initSize) && initSize > 0 ? initSize : 20);

    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState(null);
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    const [filesModalOpen, setFilesModalOpen] = useState(false);
    const [filesModalProject, setFilesModalProject] = useState(null);

    // Sync pagination state to URL
    const replaceHistory = (pageNum, size, search) => {
        const params = new URLSearchParams(location.search);
        if (!isNaN(pageNum)) params.set('page', pageNum);
        if (!isNaN(size)) params.set('size', size);
        if (search) params.set('search', search);
        history.replace({ pathname: location.pathname, search: params.toString() });
    };


    const openFilesModal = (project) => {
        setFilesModalProject(project || null);
        setFilesModalOpen(true);
    };

    const closeFilesModal = () => {
        setFilesModalOpen(false);
        setFilesModalProject(null);
    };


    useEffect(() => {
        dispatch(teamGetAllAction());
    }, [dispatch]);

    // Dùng ref để bỏ qua lần mount đầu tiên (tránh reset page từ URL về 1)
    const filterMountedRef = useRef(false);
    useEffect(() => {
        if (filterMountedRef.current) {
            setPage(1);
            replaceHistory(1, pageSize, searchText);
        } else {
            filterMountedRef.current = true;
        }
    }, [searchText, startDateFilter, endDateFilter, selectedTeamId, selectedStatus]);

    const buildPagingParams = () => {
        const params = {
            pageIndex: page,
            pageSize: pageSize
        };

        const keyword = searchText.trim();
        if (keyword) params.keyword = keyword;

        if (startDateFilter) params.startDateFrom = startDateFilter;
        if (endDateFilter) params.endDateTo = endDateFilter;

        if (selectedTeamId) {
            params.teamId = Number(selectedTeamId);
        }

        if (selectedStatus !== '') {
            params.status = Number(selectedStatus);
        }

        return params;
    };

    useEffect(() => {
        dispatch(projectGetAllPagingAction(buildPagingParams()));
    }, [
        dispatch,
        page,
        pageSize,
        searchText,
        startDateFilter,
        endDateFilter,
        selectedTeamId,
        selectedStatus
    ]);

    const handleAdd = () => {
        history.push(URI_INTERNAL.PROJECT_CREATE_PATH);
    };

    const deleteAction = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name);
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = async () => {
        if (!idWantDelete) return;

        try {
            await dispatch(projectDeleteAction(idWantDelete)).unwrap();
            toast.success(messageToast.messageToastDeleteSuccess);
        } catch (err) {
            console.error('Xoá dự án lỗi:', err);

            let msg =
                (err && err.message) ||
                (typeof err === 'string' ? err : '') ||
                (err && err.title) ||
                '';

            if (!msg && err && err.errors) {
                msg = Object.values(err.errors).flat().join('; ');
            }

            toast.error(
                msg
                    ? `${messageToast.messageToastTryCatch}: ${msg}`
                    : messageToast.messageToastTryCatch
            );
        } finally {
            setOpenConfirmDeleteModal(false);
            setIdWantDelete(null);
            setConfirmDelMessage('');
        }
    };

    const cancelDelete = () => {
        setOpenConfirmDeleteModal(false);
        setIdWantDelete(null);
        setConfirmDelMessage('');
    };

    const handleChangeStatus = async (project) => {
        const isDone = isProjectDone(project.trangThai);
        const targetStatus = isDone ? 0 : 1;

        const confirmText = isDone
            ? 'Chuyển dự án này về trạng thái ĐANG THỰC HIỆN?'
            : 'Đánh dấu dự án này là HOÀN THÀNH?';

        if (!window.confirm(confirmText)) return;

        try {
            await dispatch(
                projectUpdateStatusAction({
                    id: project.id,
                    status: targetStatus
                })
            ).unwrap();

            await dispatch(projectGetAllPagingAction(buildPagingParams()));

            toast.success(
                isDone
                    ? 'Đã chuyển dự án về trạng thái Đang thực hiện'
                    : 'Đã chuyển dự án sang trạng thái Hoàn thành'
            );
        } catch (err) {
            console.error('Cập nhật trạng thái lỗi:', err);

            let msg =
                (err && err.message) ||
                (typeof err === 'string' ? err : '') ||
                (err && err.title) ||
                '';

            if (!msg && err && err.errors) {
                msg = Object.values(err.errors).flat().join('; ');
            }

            toast.error(
                msg
                    ? `${messageToast.messageToastTryCatch}: ${msg}`
                    : messageToast.messageToastTryCatch
            );
        }
    };

    const changePage = (number) => {
        setPage(number);
        replaceHistory(number, pageSize, searchText);
    };


    const onChangeShowNumber = (e) => {
        const newSize = parseInt(e.target.value, 10) || 20;
        setPageSize(newSize);
        setPage(1);
        replaceHistory(1, newSize, searchText);
    };

    const handleResetFilter = () => {
        setSearchText('');
        setStartDateFilter('');
        setEndDateFilter('');
        setSelectedTeamId('');
        setSelectedStatus('');
    };

    const renderStatusBadge = (p) => {
        const isDone = isProjectDone(p.trangThai);
        const label =
            mapTrangThaiDuAnLabel(p.trangThai) ||
            (isDone ? 'Hoàn thành' : 'Đang thực hiện');

        return (
            <span
                className={`badge ${isDone ? 'badge-success' : 'badge-warning'}`}
                style={BIG_BADGE_STYLE}
            >
                {label}
            </span>
        );
    };

    const renderProgressBadge = (p) => {
        const isDone = isProjectDone(p.trangThai);
        const value = getProjectEffectiveProgress(p);

        return (
            <span
                className={`badge ${isDone ? 'badge-success' : 'badge-info'}`}
                style={BIG_BADGE_STYLE}
            >
                {value}%
            </span>
        );
    };

    const makeFileUrl = (fileUrl) =>
    fileUrl && fileUrl.startsWith('http')
        ? fileUrl
        : `${BaseGatewayImage}${fileUrl || ''}`

    const getFileExt = (f) => {
        const raw = (f?.name || f?.fileUrl || '').toString().toLowerCase()
        const clean = raw.split('?')[0].split('#')[0]
        const i = clean.lastIndexOf('.')
        return i >= 0 ? clean.slice(i + 1) : ''
        }

    const INLINE_PREVIEW_EXTS = new Set([
        'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg',
        'pdf'
        ])

    const canInlinePreview = (f) => INLINE_PREVIEW_EXTS.has(getFileExt(f))

    const handleDownloadProjectFile = async (f) => {
        try {
            const urls = [PROJECT_API.DOWNLOAD_FILE(f.id)]
            await downloadFileByUrls(urls, f.name || 'tep-dinh-kem')
        } catch (e) {
            console.error('Download error:', e)
            toast.error('Không thể tải tệp. Vui lòng thử lại hoặc liên hệ quản trị.')
            }
    }

    const renderFilesCell = (p) => {
        const files = Array.isArray(p?.files) ? p.files : [];
        const count = files.length;

        return (
            <button
                type="button"
                className="btn btn-sm btn-link p-0"
                onClick={() => openFilesModal(p)}
                style={{ whiteSpace: 'nowrap' }}
                title={count > 0 ? `Có ${count} tệp` : 'Không có tệp'}
            >
                {count > 0 ? `Tệp (${count})` : 'Tệp'}
            </button>
        );
    };


    //const renderFilesCell = (p) => {
    //    // if (!Array.isArray(p.files) || p.files.length === 0) {
    //    //     return <span>Không có</span>
    //    // }
    //    const files = Array.isArray(p?.files) ? p.files : [];
    //    const count = files.length;
    //    return (
    //        <div style={{ textAlign: 'left' }}>
    //        {p.files.map((f) => {
    //            const directUrl = makeFileUrl(f.fileUrl)
    //            const displayName = f.name || 'Tệp đính kèm'
    //            const inline = canInlinePreview(f)

    //            // Ảnh/PDF: click để xem trực tiếp
    //            if (inline) {
    //            return (
    //                <div key={f.id} className="mb-1">
    //                <a
    //                    href={directUrl}
    //                    target="_blank"
    //                    rel="noopener noreferrer"
    //                    title={`Xem ${displayName}`}
    //                >
    //                    {displayName}
    //                </a>
    //                </div>
    //            )
    //            }

    //            // Word/Excel/PPT/...: click là tải luôn (đúng tên, không GUID)
    //            return (
    //            <div key={f.id} className="mb-1">
    //                <button
    //                type="button"
    //                className="btn btn-link p-0"
    //                title={`Tải ${displayName}`}
    //                onClick={() => handleDownloadProjectFile(f)}
    //                style={{ textAlign: 'left' }}
    //                >
    //                {displayName}
    //                </button>
    //            </div>
    //            )
    //        })}
    //        </div>
    //    )
    //};

    const currentList = Array.isArray(list) ? list : [];
    const totalItems = totalRecords || 0;

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">Quản lý Dự án</h3>
                        </div>

                        <div className="card-body">
                            <div className="d-flex justify-content-between mb-3">
                                <form className="form-inline">
                                    <label className="mr-2">Hiển thị :</label>
                                    <select
                                        className="form-control"
                                        value={pageSize}
                                        onChange={onChangeShowNumber}
                                    >
                                        {objectShowNumber.map((item, idx) => (
                                            <option
                                                key={idx}
                                                value={item.value}
                                            >
                                                {item.text}
                                            </option>
                                        ))}
                                    </select>
                                </form>

                                <button
                                    type="button"
                                    className="btn btn-info"
                                    onClick={handleAdd}
                                >
                                    Thêm mới
                                </button>
                            </div>

                            {/* Bộ lọc */}
                            <div className="card mb-3">
                                <div className="card-body">
                                    <div className="form-row align-items-end">
                                        <div className="form-group col-xl-2 col-lg-3 col-md-4 mb-2">
                                            <label>Tìm kiếm dự án</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Nhập tên dự án..."
                                                value={searchText}
                                                onChange={(e) => setSearchText(e.target.value)}
                                            />
                                        </div>

                                        <div className="form-group col-xl-2 col-lg-3 col-md-4 mb-2">
                                            <label>Ngày bắt đầu dự kiến</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={startDateFilter}
                                                onChange={(e) => setStartDateFilter(e.target.value)}
                                            />
                                        </div>

                                        <div className="form-group col-xl-2 col-lg-3 col-md-4 mb-2">
                                            <label>Ngày kết thúc dự kiến</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={endDateFilter}
                                                onChange={(e) => setEndDateFilter(e.target.value)}
                                            />
                                        </div>

                                        <div className="form-group col-xl-2 col-lg-3 col-md-4 mb-2">
                                            <label>Nhóm thực hiện</label>
                                            <select
                                                className="form-control"
                                                value={selectedTeamId}
                                                onChange={(e) => setSelectedTeamId(e.target.value)}
                                            >
                                                <option value="">-- Tất cả nhóm --</option>
                                                {teams.map((t) => (
                                                    <option key={t.id} value={t.id}>
                                                        {t.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="form-group col-xl-2 col-lg-3 col-md-4 mb-2">
                                            <label>Trạng thái dự án</label>
                                            <select
                                                className="form-control"
                                                value={selectedStatus}
                                                onChange={(e) => setSelectedStatus(e.target.value)}
                                            >
                                                <option value="">-- Tất cả trạng thái --</option>
                                                {TRANG_THAI_DU_AN_OPTIONS.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="form-group col-xl-2 col-lg-3 col-md-4 mb-2 d-flex justify-content-end">
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={handleResetFilter}
                                            >
                                                Xóa lọc
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {loading ? (
                                <div>Đang tải...</div>
                            ) : (
                                <>
                                        <div className="table-responsive project-table-wrap">
                                        <Table
                                            striped
                                            bordered
                                            hover
                                        >
                                            {/* ✅ Ép độ rộng từng cột */}
                                                <colgroup>
                                                    <col style={{ width: '5%' }} />   {/* STT */}
                                                    <col style={{ width: '14%' }} />  {/* Tên */}
                                                    <col style={{ width: '26%' }} />  {/* Mô tả */}
                                                    <col style={{ width: '10%' }} />  {/* Nhóm */}
                                                    <col style={{ width: '10%' }} />   {/* Trạng thái */}
                                                    <col style={{ width: '7%' }} />   {/* Tiến độ */}
                                                    <col style={{ width: '8%' }} />  {/* Thời gian dự kiến */}
                                                    <col style={{ width: '8%' }} />   {/* Thực tế */}
                                                    <col style={{ width: '7%' }} />   {/* Tệp */}
                                                    <col style={{ width: '5%' }} />   {/* Chức năng */}
                                                </colgroup>

                                            <thead>
                                                <tr>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>STT</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Tên dự án</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Mô tả</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Nhóm thực hiện</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Trạng thái</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Tiến độ</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Thời gian dự kiến</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Hoàn thành thực tế</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Tệp đính kèm</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Chức năng</th>
                                                </tr>
                                            </thead>

                                            {currentList && currentList.length > 0 ? (
                                                <tbody>
                                                    {currentList.map((p, index) => {
                                                        const isMissingExpectedEndDate = hasMissingExpectedEndDate(p);
                                                        const rowCellStyle = isMissingExpectedEndDate ? MISSING_END_DATE_CELL_STYLE : {};

                                                        return (
                                                            <tr key={p.id}>
                                                                <td style={rowCellStyle}>
                                                                    {index + 1 + (page - 1) * pageSize}
                                                                </td>

                                                                <td
                                                                    style={{
                                                                        textAlign: 'left',
                                                                        whiteSpace: 'normal',
                                                                        wordBreak: 'break-word',
                                                                        ...rowCellStyle
                                                                    }}
                                                                >
                                                                    {p.name}
                                                                </td>

                                                                <td
                                                                    style={{
                                                                        textAlign: 'left',
                                                                        whiteSpace: 'pre-wrap',
                                                                        wordBreak: 'break-word',
                                                                        ...rowCellStyle
                                                                    }}
                                                                >
                                                                    {p.moTa}
                                                                </td>

                                                                <td style={rowCellStyle}>
                                                                    {Array.isArray(p.teamNames) && p.teamNames.length > 0
                                                                        ? p.teamNames.join(', ')
                                                                        : 'Chưa gán nhóm'}
                                                                </td>

                                                                <td
                                                                    style={{
                                                                        cursor: 'pointer',
                                                                        textAlign: 'center',
                                                                        ...rowCellStyle
                                                                    }}
                                                                    title="Click để chuyển trạng thái Đang thực hiện / Hoàn thành"
                                                                    onClick={() => handleChangeStatus(p)}
                                                                >
                                                                    {renderStatusBadge(p)}
                                                                </td>

                                                                <td
                                                                    style={{
                                                                        textAlign: 'center',
                                                                        ...rowCellStyle
                                                                    }}
                                                                >
                                                                    {renderProgressBadge(p)}
                                                                </td>

                                                                <td
                                                                    style={{
                                                                        whiteSpace: 'normal',
                                                                        ...rowCellStyle
                                                                    }}
                                                                >
                                                                    {(p.startDate || p.endDate) ? (
                                                                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                                                                            <div>{formatDate(p.startDate)}</div>
                                                                            -
                                                                            <div>{formatDate(p.endDate)}</div>
                                                                        </div>
                                                                    ) : (
                                                                        ''
                                                                    )}
                                                                </td>

                                                                <td
                                                                    style={{
                                                                        whiteSpace: 'normal',
                                                                        ...rowCellStyle
                                                                    }}
                                                                >
                                                                    {formatDate(p.actualEndDate)}
                                                                </td>

                                                                <td style={rowCellStyle}>
                                                                    {renderFilesCell(p)}
                                                                </td>

                                                                <td
                                                                    style={{
                                                                        whiteSpace: 'nowrap',
                                                                        ...rowCellStyle
                                                                    }}
                                                                >
                                                                    <Dropdown align="end">
                                                                        <Dropdown.Toggle
                                                                            variant="outline-secondary"
                                                                            size="sm"
                                                                            id={`project-actions-${p.id}`}
                                                                        >
                                                                            Thao tác
                                                                        </Dropdown.Toggle>

                                                                        <Dropdown.Menu>
                                                                            <Dropdown.Item
                                                                                as={NavLink}
                                                                                to={`${URI_INTERNAL.PROJECT_DETAIL_PATH}?id=${p.id}`}
                                                                            >
                                                                                Xem chi tiết
                                                                            </Dropdown.Item>

                                                                            <Dropdown.Item
                                                                                as={NavLink}
                                                                                to={`${URI_INTERNAL.PROJECT_EDIT_PATH}?id=${p.id}`}
                                                                            >
                                                                                Sửa
                                                                            </Dropdown.Item>

                                                                            <Dropdown.Divider />

                                                                            <Dropdown.Item
                                                                                as="button"
                                                                                className="w-100 text-left text-danger"
                                                                                onClick={() => deleteAction(p.id, p.name)}
                                                                            >
                                                                                Xóa
                                                                            </Dropdown.Item>
                                                                        </Dropdown.Menu>
                                                                    </Dropdown>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            ) : (
                                                <tbody>
                                                    <tr className="txt-center">
                                                        <td colSpan="10">Không có dữ liệu</td>
                                                    </tr>
                                                </tbody>
                                            )}

                                            <tfoot>
                                                <tr>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>STT</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Tên dự án</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Mô tả</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Nhóm thực hiện</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Trạng thái</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Tiến độ</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Thời gian dự kiến</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Hoàn thành thực tế</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Tệp đính kèm</th>
                                                    <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Chức năng</th>
                                                </tr>
                                            </tfoot>
                                            </Table>
                                        </div>

                                    <div className="dataTables_paginate paging_simple_numbers">
                                        <Pagination
                                            itemClass="page-item"
                                            linkClass="page-link"
                                            innerClass="pagination pagination-sm no-margin pull-right"
                                            activePage={page}
                                            itemsCountPerPage={Number(pageSize)}
                                            totalItemsCount={totalItems}
                                            pageRangeDisplayed={5}
                                            onChange={changePage}
                                        />
                                    </div>
                                </>
                            )}

                            <ConfirmModal
                                isOpen={openConfirmDeleteModal}
                                confirmType="delete"
                                handleAccept={acceptDelete}
                                handleCancel={cancelDelete}
                                content={confirmDelMessage}
                            />

                            {/* ===== MODAL FILES ===== */}
                            <Modal show={filesModalOpen} onHide={closeFilesModal} size="md" backdrop={false}>
                                <Modal.Header closeButton>
                                    <Modal.Title>
                                        Tệp đính kèm {filesModalProject?.name ? `- ${filesModalProject.name}` : ''}
                                    </Modal.Title>
                                </Modal.Header>

                                <Modal.Body>
                                    {(() => {
                                        const files = Array.isArray(filesModalProject?.files) ? filesModalProject.files : [];

                                        if (!filesModalProject) {
                                            return <div className="text-muted">Không có dữ liệu.</div>;
                                        }

                                        if (files.length === 0) {
                                            return <div className="text-muted">Không có tệp đính kèm.</div>;
                                        }

                                        return (
                                            <ul className="list-unstyled mb-0">
                                                {files.map((f) => {
                                                    const fileUrl = f?.fileUrl || f?.FileUrl || '';
                                                    const directUrl = makeFileUrl(fileUrl);
                                                    const displayName = f?.name || f?.fileName || f?.FileName || 'Tệp đính kèm';

                                                    // ưu tiên kiểm tra ext theo tên hiển thị + fileUrl
                                                    const inline = canInlinePreview({ name: displayName, fileUrl });

                                                    return (
                                                        <li key={f.id} className="d-flex align-items-center justify-content-between mb-2">
                                                            <div style={{ minWidth: 0 }} className="mr-2">
                                                                {inline ? (
                                                                    <a
                                                                        href={directUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title={displayName}
                                                                        className="text-truncate d-inline-block"
                                                                        style={{ maxWidth: 380 }}
                                                                    >
                                                                        {displayName}
                                                                    </a>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-link p-0 text-truncate"
                                                                        style={{ maxWidth: 380, textAlign: 'left' }}
                                                                        title={`Tải ${displayName}`}
                                                                        onClick={() => handleDownloadProjectFile(f)}
                                                                    >
                                                                        {displayName}
                                                                    </button>
                                                                )}
                                                            </div>

                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => handleDownloadProjectFile(f)}
                                                            >
                                                                Tải
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        );
                                    })()}
                                </Modal.Body>

                                <Modal.Footer>
                                    <button className="btn btn-secondary" onClick={closeFilesModal}>Đóng</button>
                                </Modal.Footer>
                            </Modal>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Project;

// nghiaabuiipc test branch new 203
// nghiaabuii test branch new 2203